/**
 * The context graph's visual vocabulary — one place, so the cards, the legend
 * swatches and the edge strokes cannot disagree.
 *
 * Every colour is returned as a `var(--cg-*)` reference resolved by
 * `contextGraph.css`; nothing here hard-codes a hex. That is what makes the
 * dark theme a single CSS block instead of a second copy of this table, and it
 * is why the legend can render a swatch that is *the same value* the node uses
 * rather than an approximation of it.
 */

import type {
	ContextEdge,
	ContextGraph,
	ContextNode,
	ContextNodeKind,
} from "../../types/contextGraph";

/** Resolved paint for one card. Consumed as inline style by node and legend. */
export interface NodeVisual {
	background: string;
	borderColor: string;
	borderWidth: number;
	color: string;
	/** Sublabel colour — inherits the card text when the card is muted. */
	sublabelColor: string;
	/**
	 * The 4px bar down the card's left edge, and the legend's swatch stripe.
	 *
	 * Eight kinds separated by tint alone is a colour key the reader has to
	 * memorise; a saturated accent against a near-neutral card is legible at a
	 * glance and survives the 45% dimming a card outside the focus gets.
	 */
	accent: string;
}

const token = (name: string): string => `var(--cg-${name})`;

/** Legend copy + the swatch each row shows. Order is the mockup's legend row. */
export interface LegendItem {
	kind: ContextNodeKind;
	label: string;
	/** The card sub-style this row names, where a kind has more than one. */
	variant?: string | null;
	/** The status a verdict swatch is drawn with. */
	status?: string | null;
}

/**
 * Every paint a card can be drawn in, in reading order. This is the CATALOG;
 * `legendFor` picks the rows a given canvas actually uses. A room graph is
 * companies and paperwork and has no EBITDA, no covenant and no verdict — a
 * legend that listed them anyway was describing a different picture.
 *
 * Document rows are per accent family, because on a room graph the accent
 * bar is the only thing telling a credit agreement from a valuation.
 */
export const LEGEND_ITEMS: readonly LegendItem[] = [
	{ kind: "company", label: "Company" },
	{ kind: "fund", label: "Fund / period" },
	{ kind: "document", label: "Credit agreement", variant: "credit_agreement" },
	{ kind: "document", label: "Financial statements", variant: "financial_statements" },
	{ kind: "document", label: "Compliance certificate", variant: "compliance_certificate" },
	{ kind: "document", label: "Valuation", variant: "valuation_report" },
	{ kind: "document", label: "Document" },
	{ kind: "document", label: "Superseded draft", status: "superseded" },
	{ kind: "clause", label: "Clause" },
	{ kind: "metric", label: "EBITDA", variant: "ebitda" },
	{ kind: "metric", label: "Metric" },
	{ kind: "metric", label: "Scenario", variant: "scenario" },
	{ kind: "covenant", label: "Covenant" },
	{ kind: "metric", label: "Cushion", variant: "headroom" },
	{ kind: "status", label: "Status", status: "compliant" },
];

/**
 * The paint a node is drawn in, as a key: the same partition `baseVisual`
 * switches on, so two nodes with one key look the same and a legend row
 * matches a node exactly when it would be painted like the row's swatch.
 */
export function paintKey(node: NodeVisualInput): string {
	switch (node.kind) {
		case "fund":
		case "period":
			return "fund";
		case "document": {
			if (node.status === "superseded") return "document:superseded";
			const family = DOCUMENT_ACCENT_FAMILY[node.variant ?? ""];
			return family ? `document:${family}` : "document";
		}
		case "metric": {
			const variant = node.variant;
			return variant === "ebitda" || variant === "scenario" || variant === "headroom"
				? `metric:${variant}`
				: "metric";
		}
		case "status":
			return "status";
		default:
			return node.kind;
	}
}

/**
 * The legend rows for what is on THIS canvas: one per paint that appears,
 * in catalog order; the verdict swatches only for verdicts present; the
 * reasoning-path line only when an edge is on the path.
 */
export interface LiveLegend {
	items: LegendItem[];
	statuses: string[];
	showPath: boolean;
}

export function legendFor(graph: Pick<ContextGraph, "nodes" | "edges">): LiveLegend {
	const present = new Set(graph.nodes.map((node) => paintKey(node)));
	const items = LEGEND_ITEMS.filter((item) =>
		present.has(paintKey({ kind: item.kind, status: item.status ?? null, variant: item.variant ?? null })),
	);
	const seen = new Set<string>();
	const statuses = graph.nodes
		.filter((node) => node.kind === "status" && node.status)
		.map((node) => node.status as string)
		.filter((status) => (seen.has(status) ? false : (seen.add(status), true)));
	const order = STATUS_SWATCHES;
	statuses.sort((a, b) => order.indexOf(a) - order.indexOf(b));
	return {
		items,
		statuses,
		showPath: graph.edges.some((edge) => edge.on_path),
	};
}

/** The three verdicts the status swatch shows, worst last. */
export const STATUS_SWATCHES: readonly string[] = ["compliant", "watch", "breach"];

/** Swatch paint for legend row `index`, drawn from the same table as the cards. */
export function legendVisual(index: number): NodeVisual {
	const item = LEGEND_ITEMS[index];
	return nodeVisual({
		kind: item.kind,
		status: item.status ?? null,
		variant: item.variant ?? null,
	});
}

/**
 * `doc_type` -> the accent family a document card is tinted with.
 *
 * The backend sends the type as the node's `variant`, because a document node
 * IS one file and its type is an attribute of that file, not a second kind. The
 * card is named after the file; this is what still tells a reader, at a glance
 * down a column, which of them is the legal agreement and which is the
 * reporting. Types absent here keep the neutral document accent rather than
 * being given a colour that means nothing.
 */
const DOCUMENT_ACCENT_FAMILY: Record<string, string> = {
	credit_agreement: "agreement",
	term_sheet: "agreement",
	compliance_certificate: "certificate",
	covenant_compliance: "certificate",
	financial_statements: "financials",
	reporting_pack: "financials",
	operating_statement: "financials",
	financial_model: "financials",
	valuation_report: "valuation",
};

/** The subset of a node the paint actually depends on. */
export type NodeVisualInput = Pick<ContextNode, "kind" | "status" | "variant">;

/**
 * Paint for a node card.
 *
 * The reasoning path is drawn on the EDGES and leaves the card's paint alone.
 * An earlier cut gave every card on the path a teal border; once the path ran
 * through most of the figures, that turned the whole canvas teal and made a
 * grey "Metric" swatch describe cards that were visibly not grey. The card
 * says what it is; the line through it says it was walked. (`onPath` is kept
 * in the signature so callers do not change; the node component adds a faint
 * lift for path cards on its own.)
 */
export function nodeVisual(node: NodeVisualInput, _onPath = false): NodeVisual {
	return baseVisual(node);
}

function baseVisual(node: NodeVisualInput): NodeVisual {
	const paint = (name: string, sublabel = token("sublabel")): NodeVisual => ({
		background: token(`${name}-bg`),
		borderColor: token(`${name}-border`),
		borderWidth: 1,
		color: token(`${name}-text`),
		sublabelColor: sublabel,
		accent: token(`${name}-accent`),
	});

	switch (node.kind) {
		case "company":
			// Filled card: the sublabel has to lift off the dark teal, so it
			// borrows the card's own text colour rather than the grey.
			return paint("company", token("company-text"));
		case "fund":
		case "period":
			return paint("fund");
		case "document": {
			if (node.status === "superseded") {
				return paint("superseded", token("superseded-text"));
			}
			const visual = paint("document");
			const family = DOCUMENT_ACCENT_FAMILY[node.variant ?? ""];
			return family ? { ...visual, accent: token(`doc-${family}-accent`) } : visual;
		}
		case "clause":
			return paint("clause");
		case "metric": {
			const visual = paint("metric");
			switch (node.variant) {
				case "ebitda":
					return { ...visual, background: token("ebitda-bg"), accent: token("ebitda-accent") };
				// Headroom is a figure ABOUT a covenant: it borrows the covenant
				// family's ground so it reads beside the level it measures.
				case "headroom":
					return paint("headroom");
				// A scenario is the same quantity under the agreement's terms.
				case "scenario":
					return paint("scenario");
				default:
					return visual;
			}
		}
		case "covenant":
			return paint("covenant");
		case "status":
			if (node.status === "breach") return paint("breach", token("breach-text"));
			if (node.status === "compliant") return paint("compliant", token("compliant-text"));
			return paint("covenant", token("covenant-text"));
	}
}

/** Stroke paint for an edge. Context edges recede; the path asserts itself. */
export interface EdgeVisual {
	stroke: string;
	strokeWidth: number;
	labelColor: string;
}

export function edgeVisual(edge: Pick<ContextEdge, "on_path">): EdgeVisual {
	return edge.on_path
		? { stroke: token("path"), strokeWidth: 2.5, labelColor: token("path") }
		: { stroke: token("edge"), strokeWidth: 1, labelColor: token("edge-label") };
}
